import React, { CSSProperties, useState } from 'react'
import { Box, Modal } from '@mui/material'
import { getRotatedBox, NaturalSize } from '../../helpers/imageRotation'

type Props = {
  imageUrl: string
  /** 表示中の角度（deg）。インラインのビューアと同じ角度で出す */
  rotation: number
  /** インライン画像で測った自然サイズ。ここでは読み込みを待たない */
  size: NaturalSize
  onClose: () => void
}

/**
 * 回転後の外接矩形 (bw × bh) を wrapper の大きさにし、その中央に画像を置いて回す。
 * 「画面に収める」モードは vw/vh の min() で倍率を CSS に任せ、
 * 「原寸」モードは自然サイズの px をそのまま使う（外側の overflow:auto で隅まで到達）。
 */
export const getBoxStyles = (
  size: NaturalSize,
  rotation: number,
  isActualSize: boolean
): { wrapper: CSSProperties; image: CSSProperties } => {
  const { w, h } = size
  const { bw, bh } = getRotatedBox(size, rotation)
  const image: CSSProperties = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
  }
  if (isActualSize) {
    return {
      wrapper: { width: bw, height: bh },
      image: { ...image, width: w, height: h },
    }
  }
  // 倍率 s = min(100vw / bw, 100vh / bh) を各辺に掛けたもの
  return {
    wrapper: {
      width: `min(100vw, ${(100 * bw) / bh}vh)`,
      height: `min(${(100 * bh) / bw}vw, 100vh)`,
    },
    image: {
      ...image,
      width: `min(${(100 * w) / bw}vw, ${(100 * w) / bh}vh)`,
      height: `min(${(100 * h) / bw}vw, ${(100 * h) / bh}vh)`,
    },
  }
}

/**
 * 処方箋画像の原寸ビューア。開いている間だけ mount する（閉じれば状態も消える）。
 * 背景クリックと Esc で閉じ、画像クリックで「画面に収める」と「原寸」を切り替える
 * （pages/guide/components/Figure.tsx と同じ 2 状態）。
 */
export const PrescriptionImageLightbox: React.FC<Props> = ({
  imageUrl,
  rotation,
  size,
  onClose,
}) => {
  const [isActualSize, setIsActualSize] = useState(false)
  const styles = getBoxStyles(size, rotation, isActualSize)

  return (
    <Modal open onClose={onClose}>
      <Box
        data-testid="prescription-image-lightbox"
        onClick={onClose}
        sx={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          overflow: isActualSize ? 'auto' : 'hidden',
          outline: 'none',
        }}
      >
        <Box
          onClick={(event) => {
            event.stopPropagation()
            setIsActualSize(!isActualSize)
          }}
          sx={{
            position: 'relative',
            m: 'auto',
            flexShrink: 0,
            cursor: isActualSize ? 'zoom-out' : 'zoom-in',
          }}
          style={styles.wrapper}
        >
          <img src={imageUrl} alt="" style={styles.image} />
        </Box>
      </Box>
    </Modal>
  )
}
